import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail,
  CheckCircle2,
  AlertTriangle,
  Send,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";

/**
 * EmailDiagnosticCard — admin-only tool to verify that winner emails are
 * properly configured AND actually delivered via Resend. Shows current
 * environment config + warnings and lets admin fire a real test email.
 */
export default function EmailDiagnosticCard() {
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sendingMystery, setSendingMystery] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api
      .get("/admin/diagnostic/email")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ resend_configured: false, warnings: [] }));
  }, []);

  async function sendTest() {
    if (!email.trim() || sending) return;
    setSending(true);
    setLastResult(null);
    try {
      const r = await api.post("/admin/diagnostic/email", {
        to_email: email.trim(),
      });
      setLastResult({ ok: true, kind: "win", ...r.data });
      toast.success(`Email gagnant envoyé à ${email.trim()}`);
    } catch (e) {
      const msg = formatApiError(e);
      setLastResult({ ok: false, kind: "win", error: msg });
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }

  async function sendMysteryTest() {
    if (!email.trim() || sendingMystery) return;
    setSendingMystery(true);
    setLastResult(null);
    try {
      const r = await api.post("/admin/diagnostic/mystery-email", {
        to_email: email.trim(),
      });
      setLastResult({ ok: true, kind: "mystery", ...r.data });
      toast.success(`Email Mystery Box envoyé à ${email.trim()}`);
    } catch (e) {
      const msg = formatApiError(e);
      setLastResult({ ok: false, kind: "mystery", error: msg });
      toast.error(msg);
    } finally {
      setSendingMystery(false);
    }
  }

  if (!status) return null;

  const isPreviewFrontend =
    status.frontend_url?.includes("preview.emergentagent.com");
  const sandboxSender = status.sender_email?.endsWith("@resend.dev");
  const allGood =
    status.resend_configured &&
    !isPreviewFrontend &&
    !sandboxSender &&
    (status.warnings || []).length === 0;

  return (
    <div className="qrise-card p-6" data-testid="email-diagnostic-card">
      <div className="flex items-start gap-3 mb-4">
        <div
          className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${
            allGood
              ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
              : status.resend_configured
                ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                : "bg-rose-500/15 text-rose-600 dark:text-rose-400"
          }`}
        >
          <Mail className="h-5 w-5" />
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
            Diagnostic
          </div>
          <div className="text-base font-semibold tracking-tight">
            Email des gagnants
          </div>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Vérifie que les emails envoyés aux gagnants partent bien depuis
            cet environnement (Resend + domaine + URL des liens).
          </p>
        </div>
      </div>

      {/* Config grid */}
      <div className="grid sm:grid-cols-2 gap-3 text-[13px] mb-4">
        <ConfigRow
          label="Resend API key"
          value={status.resend_configured ? "Configurée" : "Manquante"}
          ok={status.resend_configured}
        />
        <ConfigRow
          label="Expéditeur"
          value={
            status.sender_name
              ? `${status.sender_name} <${status.sender_email}>`
              : status.sender_email
          }
          ok={!sandboxSender}
          warn={sandboxSender}
          warnHint="Domaine sandbox — Resend risque de ne pas délivrer."
        />
        <ConfigRow
          label="Front-end URL (liens de l'email)"
          value={status.frontend_url || "Non configurée"}
          ok={!!status.frontend_url && !isPreviewFrontend}
          warn={isPreviewFrontend}
          warnHint="Pointe vers la preview — à changer vers votre domaine de prod."
        />
        <ConfigRow
          label="Domaine d'envoi"
          value={
            status.sender_email?.split("@")[1] || "—"
          }
          ok={status.resend_configured && !sandboxSender}
        />
      </div>

      {/* Warnings */}
      {status.warnings?.length > 0 && (
        <div
          className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3"
          data-testid="email-diagnostic-warnings"
        >
          <div className="flex items-center gap-2 text-xs font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-2">
            <AlertTriangle className="h-3.5 w-3.5" />
            Attention
          </div>
          <ul className="space-y-1.5 text-[12.5px] text-amber-900 dark:text-amber-200">
            {status.warnings.map((w, i) => (
              <li key={i}>• {w}</li>
            ))}
          </ul>
        </div>
      )}

      {allGood && (
        <div
          className="mb-4 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 flex items-center gap-2 text-[13px] text-emerald-900 dark:text-emerald-200"
          data-testid="email-diagnostic-ok"
        >
          <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          Configuration optimale — les emails partent et les liens pointent vers votre domaine de production.
        </div>
      )}

      {/* Test sender */}
      <div className="border-t border-border pt-4">
        <Label className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2 block">
          Envoyer un email de test
        </Label>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="email"
            placeholder="vous@exemple.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-10 rounded-xl flex-1"
            data-testid="email-diagnostic-input"
          />
          <Button
            onClick={sendTest}
            disabled={!email.trim() || sending || sendingMystery || !status.resend_configured}
            className="h-10 rounded-xl"
            data-testid="email-diagnostic-send-btn"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1.5" />
            )}
            Email gagnant
          </Button>
          <Button
            onClick={sendMysteryTest}
            disabled={!email.trim() || sending || sendingMystery || !status.resend_configured}
            variant="outline"
            className="h-10 rounded-xl"
            data-testid="email-diagnostic-mystery-btn"
          >
            {sendingMystery ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-1.5" />
            )}
            Email Mystery Box
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Deux tests indépendants — chacun utilise le même pipeline Resend que le jeu correspondant.
          Reçu pour l'un et pas l'autre = problème spécifique à ce template.
        </p>

        {lastResult && (
          <div
            className={`mt-3 rounded-xl px-4 py-3 text-[13px] ${
              lastResult.ok
                ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-900 dark:text-emerald-200"
                : "bg-rose-500/10 border border-rose-500/40 text-rose-900 dark:text-rose-200"
            }`}
            data-testid="email-diagnostic-result"
          >
            {lastResult.ok ? (
              <>
                <CheckCircle2 className="inline-block h-4 w-4 mr-1.5 -mt-0.5" />
                Email <b>{lastResult.kind === "mystery" ? "Mystery Box" : "gagnant"}</b> envoyé à <b>{lastResult.to}</b>. Vérifiez votre boîte de réception (et les spams).
              </>
            ) : (
              <>
                <AlertTriangle className="inline-block h-4 w-4 mr-1.5 -mt-0.5" />
                Échec ({lastResult.kind === "mystery" ? "Mystery Box" : "gagnant"}) : {lastResult.error}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConfigRow({ label, value, ok, warn, warnHint }) {
  return (
    <div className="rounded-xl border border-border/70 bg-muted/30 px-3 py-2">
      <div className="text-[10.5px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
        {label}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5 text-[12.5px] font-medium">
        {ok ? (
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
        ) : (
          <AlertTriangle
            className={`h-3.5 w-3.5 shrink-0 ${
              warn
                ? "text-amber-600 dark:text-amber-400"
                : "text-rose-600 dark:text-rose-400"
            }`}
          />
        )}
        <span className="font-mono text-[11.5px] truncate" title={value}>
          {value}
        </span>
      </div>
      {warn && warnHint && (
        <div className="mt-1 text-[10.5px] text-amber-700 dark:text-amber-400/90">
          {warnHint}
        </div>
      )}
    </div>
  );
}
